import { useEffect, useState } from 'react';
import { CartProvider } from './contexts/CartContext';
import { ShopProvider } from './contexts/ShopContext';
import CartSwiper from './components/commons/Cart/CartSwiper';
import { Routes, Route, useLocation } from 'react-router-dom';
import Home from './pages/Home/Home';
import Header from './components/header/Header';
import MaintenanceBanner from './components/MaintenanceBanner/MaintenanceBanner';
import Footer from './components/footer/Footer';
import Products from './pages/products-page/Products';
import Catalogue from './pages/products-page/Catalogue';
import ProductDetails from './pages/single-product-page/ProductDetails';
import Propos from './pages/propos/propos';
import Blog from './pages/Blog/Blog';
import Recrutement from './pages/Recrutement/Recrutement';
import Rse from './pages/Rse/Rse';
import Aide from './pages/Aide/Aide';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';
import { WishListProvider } from './contexts/WishListContext';
import WishList from './pages/WishList/WishList';
import ProfileDetails from './pages/Profile/ProfileDetails';
import ItemHistory from './pages/Profile/ItemHistory';
import Checkout from './pages/checkout/Checkout';
import ProfileInfo from './pages/Profile/ProfileInfo';
import Address from './pages/Profile/Address';
import { Provider, useDispatch } from 'react-redux';
import { initAuthState } from './store/Reducers/auth';
import { store } from './store/store';
import AuthMiddleware from './middleware/Auth';
import Order from './pages/Order/Order';
import CGVPro from './pages/CGV/CGVPro';
import CGVParticulier from './pages/CGV/CGVParticulier';
import Mentions from './pages/Mentions/Mentions';
import ScrollToTop from './Utils/ScrollToTop';
import ResetPassword from './pages/ForgotPassword/ResetPassword';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import GoogleAnalytics from './Utils/GoogleAnalytics';
import NotFound from './pages/NotFound/NotFound';
import MicrosoftClarity from './Utils/MicrosoftClarity';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import {
  defaultTitle,
  DYNAMIC_TITLE_ROUTES,
  pageTitles,
} from './helpers/pageConfig';
import LandingPage from './pages/LandingPage/LandingPage';
import CategoryRedirectionMiddleware from './middleware/Redirections/CategoryRedirection';
import ProductRedirectionMiddleware from './middleware/Redirections/ProductRedirection';
import ReviewRedirectionMiddleware from './middleware/Redirections/ReviewRedirection';
import ProductRedirect from './pages/single-product-page/ProductRedirect';

function App() {
  const dispatch = useDispatch();
  const types = ['vehicules', 'pneus', 'jantes', 'pieces'];
  const path = useLocation();
  const [title, setTitle] = useState(defaultTitle);
  const [canonicalUrl, setCanonicalUrl] = useState('');

  useEffect(() => {
    const pathname = path.pathname;
    if (!DYNAMIC_TITLE_ROUTES.some((route) => pathname.startsWith(route))) {
      setTitle(
        pageTitles[
          Object.keys(pageTitles).find((key) =>
            pathname === '/' ? null : pathname.startsWith(key),
          )
        ]?.title || defaultTitle,
      );
    }

    setCanonicalUrl('https://stopieces-auto.fr' + pathname);
  }, [path]);

  useEffect(() => {
    dispatch(initAuthState());
  }, []);

  let routes = [];
  for (let type of types) {
    //create routes
    routes.push(<Route path={`/${type}-d-occasion`} element={<Products />} />);
    routes.push(<Route path={`/${type}/:id`} element={<ProductDetails />} />);
  }

  return (
    <Provider store={store}>
      <ShopProvider>
        <CartProvider>
          <WishListProvider>
            <HelmetProvider>
              <Helmet>
                <title>{title}</title>
                <meta
                  name="description"
                  content="Casse auto en ligne. Pièces auto d’occasion garanties 1 an. Plus de 200 000 références disponibles : jantes, moteurs, boîtes de vitesses."
                />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href={canonicalUrl} />
                <meta name="googlebot" content="index, follow" />
                <meta name="bingbot" content="index, follow" />
                <meta name="alexa" content="index, follow" />
                <meta name="yandex" content="index, follow" />
                <meta
                  name="keywords"
                  content="Casse auto, Pièces auto, Jantes, Moteurs, Boîtes de vitesses"
                />
                <meta name="author" content="Stop Pièces Auto" />
                <meta name="copyright" content="Stop Pièces Auto" />
                <meta name="language" content="French" />
                <meta name="rating" content="General" />
                <meta
                  property="og:title"
                  content={title}
                  // content="Stop Pièces Auto | Pièces détachées d'occasion garanties"
                />
                <meta
                  property="og:description"
                  content="Casse auto en ligne française. Pièces auto d'occasion garanties 1 an. Livraison rapide."
                />
                <meta property="og:url" content="https://stopieces-auto.fr/" />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                  name="twitter:title"
                  content={title}
                  // content="Stop Pièces Auto | Pièces détachées d'occasion"
                />
                <meta
                  name="twitter:description"
                  content="Casse auto en ligne française. Pièces auto d'occasion garanties 1 an."
                />
              </Helmet>
              <MaintenanceBanner />
              {path.pathname !== '/checkout' && <Header />}
              <ScrollToTop />
              <GoogleAnalytics />
              <MicrosoftClarity />
              <Routes>
                <Route path="/" element={<Home />} />
                <Route
                  path="/reset-password/:token"
                  element={<ResetPassword />}
                />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/vehicules" element={<Products />} />
                <Route path="/catalogue/*" element={<Catalogue />} />
                <Route path="/vehicules-accidentes" element={<Products />} />
                {types.map((type) => (
                  <Route
                    path={`${type}-d-occasion`}
                    element={<Products />}
                    key={`${type}-route`}
                  />
                ))}
                <Route
                  path={`/produits/:idOrSlug`}
                  element={<ProductRedirect />}
                />
                {types.map((type) => (
                  <Route
                    key={`${type}-route`}
                    path={`/${type}/:idOrSlug`}
                    element={<ProductDetails />}
                  />
                ))}
                <Route path="/cgv-pros" element={<CGVPro />} />
                <Route path="/cgv-particuliers" element={<CGVParticulier />} />
                <Route path="/mentions-legales" element={<Mentions />} />
                <Route path="/propos" element={<Propos />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/wishlist" element={<WishList />} />
                {/* <Route path="/recrutement" element={<Recrutement />} /> */}
                <Route path="/rse" element={<Rse />} />
                <Route path="/aide" element={<Aide />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/orders/:orderRef" element={<Order />} />
                <Route element={<AuthMiddleware />}>
                  {/* <Route path="/profile" element={<ProfileDetails />}></Route> */}
                  <Route
                    path="/profile/profile-details"
                    element={<ProfileDetails />}
                  />
                  <Route
                    path="/profile/profile-info"
                    element={<ProfileInfo />}
                  />
                  <Route path="/profile/addresses" element={<Address />} />
                  <Route path="/profile/orders" element={<ItemHistory />} />
                </Route>
                <Route
                  path="/catalog/category/view/*"
                  element={<CategoryRedirectionMiddleware />}
                />
                <Route
                  path="/catalog/product/view/*"
                  element={<ProductRedirectionMiddleware />}
                />
                <Route
                  path="/review/product/list/*"
                  element={<ReviewRedirectionMiddleware />}
                />
                <Route path="*" element={<NotFound />} />
                <Route path="/:slug" element={<LandingPage />} />
                {/* <Route path="*" element={<NotFound />} /> */}
              </Routes>
              <Footer />
              <ToastContainer />
              <CartSwiper />
            </HelmetProvider>
          </WishListProvider>
        </CartProvider>
      </ShopProvider>
    </Provider>
  );
}

export default App;
